import React from "react";
import {
    AutocompleteInput,
    Create,
    CreateProps,
    DateInput,
    FormDataConsumer,
    NumberInput,
    RadioButtonGroupInput,
    ReferenceInput,
    required,
    SimpleForm,
    TextInput,
    TitleProps,
    useMutation,
    useNotify,
    useRedirect,
} from "react-admin";
import { Discipline } from "model/result/discipline";
import { AdminResource } from "reactAdmin/resource/resource";
import { Capability } from "reactAdmin/capabilities";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StandardApiError from "shared/errors/standardApiError";
import { minValue } from "ra-core/esm/form/validate";
import { convertStringToNumber } from "reactAdmin/resource/result/convertStringToNumber";

interface locationState {
    profile: boolean,
    record: { userId: number, discipline: Discipline }
}

const ResultTitle = (props: TitleProps) => {
    const { t } = useTranslation();
    return <span>Create {props.record?.discipline ? t(`discipline.${props.record.discipline}`) : ''} Result</span>;
};

export const ResultCreate = (props: CreateProps) => {
    const { permissions: isAuthorized } = props;

    const { t } = useTranslation();
    const [mutate] = useMutation();
    const notify = useNotify();
    const redirect = useRedirect();
    const location = useLocation<locationState | undefined>();
    const userId = location.state?.record?.userId;
    const discipline = location.state?.record?.discipline;

    const save = React.useCallback(
        async (values, redirectTo) => {
            try {
                await mutate(
                    {
                        type: "create",
                        resource: AdminResource.results,
                        payload: { data: values },
                    },
                    { returnPromise: true }
                );
                notify(`Result created`);
                redirect(redirectTo);
            } catch (error) {
                if (error instanceof StandardApiError) {
                    return error.validationErrors;
                }
            }
        },
        [mutate, notify, redirect]
    );

    const redirectTo = userId ? (location.state?.profile ?  `/profile/shooter` : `/users/${userId}/shooter`) : 'results';

    return (
        <Create
            title={<ResultTitle />}
            {...props}
        >
            <SimpleForm
                initialValues={location.state?.record}
                save={save}
                redirect={redirectTo}
            >
                {userId === undefined && isAuthorized && isAuthorized(Capability.LIST_USERS) &&
                    <ReferenceInput source="userId" reference={AdminResource.users} filterToQuery={searchText => ({ displayName: [searchText] })}>
                        <AutocompleteInput optionText="displayName"/>
                    </ReferenceInput>
                }
                {discipline === undefined &&
                    <RadioButtonGroupInput
                        source="discipline"
                        validate={[required()]}
                        choices={Object.values(Discipline).map((item) => ({
                            id: item,
                            name: t(`discipline.${item}`),
                        }))}
                    />
                }
                <DateInput source="date" validate={[required()]} />
                <TextInput source="championshipName" validate={[required()]} />
                <FormDataConsumer source="totalScore">
                    {({ formData, ...rest }) => {
                        return (
                            <NumberInput
                                source="totalScore"
                                step={formData.discipline === Discipline.airPistol10m ? 1 : 0.1}
                                parse={convertStringToNumber(formData.discipline === Discipline.airPistol10m ? 0 : 1)}
                                validate={[required(), minValue(0)]}
                                helperText={formData.discipline === Discipline.airPistol10m ? 'Score without decimals' : 'Score with decimal numbers'}
                                fullWidth={false}
                            />
                        );
                    }}
                </FormDataConsumer>
                <NumberInput source="rank" step={1} validate={[required(), minValue(1)]} />
            </SimpleForm>
        </Create>
    );
}
